import { computed, ref, watch, type Ref } from 'vue'
import { isEqual, sortBy } from 'lodash'
import type { BuilderTab } from './types'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import {
  PASSPORT_BUILDER_GENERAL_TAB_LABEL,
  PASSPORT_BUILDER_SYNTHETIC_TAB_ID,
} from './constants'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import { clearItemTabAssignment, stampItemForTabScope } from './tabGrid'

export type UseTabManagerOptions = {
  resetWhen?: Ref<unknown>
  /** Full grid template — closing a tab moves its cells to the general tab. */
  gridTemplate?: Ref<GridFieldType[]>
  /** Avoid seeding tabs from stale `field_groups` after route/catalog change (compare first group catalog id). */
  expectedCatalogTypeId?: Ref<number>
}

type TabBaseline = {
  order: string[]
}

function syntheticTab(): BuilderTab {
  return {
    id: PASSPORT_BUILDER_SYNTHETIC_TAB_ID,
    label: PASSPORT_BUILDER_GENERAL_TAB_LABEL,
    isSynthetic: true,
    closable: false,
    renamable: false,
    draggable: false,
  }
}

function groupToTab(g: ObjectTypeGroup): BuilderTab {
  return {
    id: String(g.id),
    label: g.name,
    groupId: g.id,
    groupType: g.type,
    closable: !g.protect,
    renamable: true,
    draggable: true,
  }
}

/**
 * Builder tabs: synthetic general tab first, then all server `field_groups`,
 * plus pending tabs (POST on save).
 */
export function useTabManager(fieldGroups: Ref<ObjectTypeGroup[]>, options?: UseTabManagerOptions) {
  const tabs = ref<BuilderTab[]>([syntheticTab()])
  const activeTabId = ref(PASSPORT_BUILDER_SYNTHETIC_TAB_ID)
  const initialized = ref(false)
  const tabBaseline = ref<TabBaseline | null>(null)
  const pendingDeletedServerGroupIds = ref<number[]>([])

  const gridTemplate = options?.gridTemplate

  function captureBaseline() {
    const reals = tabs.value.filter((t) => !t.isSynthetic)
    tabBaseline.value = {
      order: reals.map((t) => (t.groupId != null ? `id:${t.groupId}` : `new:${t.id}`)),
    }
  }

  function buildFromServerGroups(groups: ObjectTypeGroup[]) {
    const tabbed = sortBy(
      groups,
      (g) => g.sort ?? 0,
    )
    tabs.value = [syntheticTab(), ...tabbed.map(groupToTab)]
    if (!tabs.value.some((t) => t.id === activeTabId.value)) {
      activeTabId.value = tabs.value[0]!.id
    }
    captureBaseline()
  }

  function syncFromServerGroups(groups: ObjectTypeGroup[]) {
    if (!groups.length) return
    buildFromServerGroups(groups)
  }

  if (options?.resetWhen) {
    watch(options.resetWhen, () => {
      tabs.value = [syntheticTab()]
      activeTabId.value = PASSPORT_BUILDER_SYNTHETIC_TAB_ID
      initialized.value = false
      tabBaseline.value = null
      pendingDeletedServerGroupIds.value = []
    })
  }

  watch(
    () => fieldGroups.value,
    (groups) => {
      if (!groups.length || initialized.value) return
      const expected = options?.expectedCatalogTypeId?.value
      const cid = groups[0]?.catalog_invent_objects_type_id
      if (expected != null && expected > 0 && cid != null && Number(cid) !== expected) {
        return
      }
      buildFromServerGroups(groups)
      initialized.value = true
    },
    { deep: false, immediate: true },
  )

  const isTabsDirty = computed(() => {
    if (pendingDeletedServerGroupIds.value.length) return true
    const reals = tabs.value.filter((t) => !t.isSynthetic)
    if (reals.some((t) => t.groupId == null)) return true
    if (!tabBaseline.value) return false
    const orderNow = reals.map((t) => (t.groupId != null ? `id:${t.groupId}` : `new:${t.id}`))
    if (!isEqual(orderNow, tabBaseline.value.order)) return true
    for (const t of reals) {
      if (t.groupId == null) continue
      const serverName = fieldGroups.value.find((g) => g.id === t.groupId)?.name
      if (serverName != null && t.label !== serverName) return true
    }
    return false
  })

  function stripGroupFromGrid(groupId: number) {
    if (!gridTemplate) return
    gridTemplate.value = gridTemplate.value.map((item) =>
      item.group_id === groupId ? clearItemTabAssignment(item) : item,
    )
  }

  function stripClientTabFromGrid(clientTabId: string) {
    if (!gridTemplate) return
    gridTemplate.value = gridTemplate.value.map((item) =>
      item.builder_tab_ref === clientTabId ? clearItemTabAssignment(item) : item,
    )
  }

  const addTab = (label: string) => {
    const id = crypto.randomUUID()
    tabs.value = [
      ...tabs.value,
      {
        id,
        label,
        groupType: 'GROUP',
        closable: true,
        renamable: true,
        draggable: true,
      },
    ]
    activeTabId.value = id
    return id
  }

  const removeTab = (id: string) => {
    if (id === PASSPORT_BUILDER_SYNTHETIC_TAB_ID) return
    const tab = tabs.value.find((t) => t.id === id)
    if (!tab) return
    if (tab.groupId != null) {
      pendingDeletedServerGroupIds.value = [...pendingDeletedServerGroupIds.value, tab.groupId]
      stripGroupFromGrid(tab.groupId)
    } else {
      stripClientTabFromGrid(tab.id)
    }
    tabs.value = tabs.value.filter((t) => t.id !== id)
    if (activeTabId.value === id) {
      activeTabId.value = tabs.value[0]?.id ?? PASSPORT_BUILDER_SYNTHETIC_TAB_ID
    }
  }

  const renameTab = (id: string, label: string) => {
    if (id === PASSPORT_BUILDER_SYNTHETIC_TAB_ID) return
    tabs.value = tabs.value.map((t) => (t.id === id ? { ...t, label } : t))
  }

  const setActiveTab = (id: string) => {
    activeTabId.value = id
  }

  /**
   * Reorder real tabs only; synthetic tab stays first. `targetTabId` is the tab dropped onto (same semantics as group reorder API target).
   */
  const reorderTabsByDrop = (draggedTabId: string, targetTabId: string) => {
    if (
      draggedTabId === PASSPORT_BUILDER_SYNTHETIC_TAB_ID ||
      targetTabId === PASSPORT_BUILDER_SYNTHETIC_TAB_ID ||
      draggedTabId === targetTabId
    ) {
      return
    }
    const syn = tabs.value[0]
    const rest = tabs.value.slice(1)
    const di = rest.findIndex((t) => t.id === draggedTabId)
    const ti = rest.findIndex((t) => t.id === targetTabId)
    if (di < 0 || ti < 0) return
    const [moved] = rest.splice(di, 1)
    let insert = ti
    if (di < ti) insert -= 1
    rest.splice(insert, 0, moved)
    tabs.value = syn ? [syn, ...rest] : rest
  }

  /** After save + refetch: rebuild tabs from server and reset dirty baseline. */
  const afterSaveRefetch = (groups: ObjectTypeGroup[]) => {
    pendingDeletedServerGroupIds.value = []
    initialized.value = true
    syncFromServerGroups(groups)
  }

  /** Сбрасывает локальный dirty-снимок после сохранения layout без повторной загрузки групп. */
  const markSaved = () => {
    pendingDeletedServerGroupIds.value = []
    initialized.value = true
    captureBaseline()
  }

  /** Map a pending tab client id to a new server group id (updates grid `builder_tab_ref` → `group_id`). */
  const promotePendingTab = (clientTabId: string, newGroupId: number) => {
    tabs.value = tabs.value.map((t) =>
      t.id === clientTabId
        ? {
            ...t,
            id: String(newGroupId),
            groupId: newGroupId,
            groupType: 'GROUP',
            closable: true,
            renamable: true,
            draggable: true,
          }
        : t,
    )
    if (activeTabId.value === clientTabId) {
      activeTabId.value = String(newGroupId)
    }
    if (gridTemplate) {
      gridTemplate.value = gridTemplate.value.map((item) =>
        item.builder_tab_ref === clientTabId
          ? stampItemForTabScope(item, { kind: 'server', groupId: newGroupId })
          : item,
      )
    }
  }

  return {
    tabs,
    activeTabId,
    pendingDeletedServerGroupIds,
    isTabsDirty,
    addTab,
    removeTab,
    renameTab,
    setActiveTab,
    reorderTabsByDrop,
    syncFromServerGroups,
    afterSaveRefetch,
    markSaved,
    promotePendingTab,
  }
}
